import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { checkinsAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Search, LogIn, LogOut, Users, Calculator, RefreshCw, Gift, ShoppingBag, ArrowLeft, Handshake, BarChart3, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import PraemienPage from '@/pages/PraemienPage';
import ShopPage from '@/pages/ShopPage';
import VerkaufPage from '@/pages/VerkaufPage';
import CloserPage from '@/pages/CloserPage';
import CheckInPage from '@/pages/CheckInPage';
import UscGastDialog from '@/components/UscGastDialog';
import InfrarotKarte from '@/components/InfrarotKarte';
import useMitgliederSuche, { trefferZusatz } from '@/hooks/useMitgliederSuche';

const TOOLS = [
  // "Abschluss heute" nur fuer Traeger von vertrieb.closer (siehe unten)
  { key: 'closer', label: 'Abschluss heute', icon: Handshake, benoetigt: 'vertrieb.closer' },
  { key: 'verkauf', label: 'Verkaufsrechner', icon: Calculator },
  { key: 'praemien', label: 'Prämien', icon: Gift },
  { key: 'shop', label: 'Shop', icon: ShoppingBag },
  // Seit 18.09.2026: die alte Check-in-Seite (Statistik, Studio-QR) lebt hier als Werkzeug weiter
  { key: 'statistik', label: 'Statistik', icon: BarChart3 },
];

// Das Tablet steht offen am Empfang: ein Werkzeug mit Kunden- und Lead-Daten
// darf dort nicht liegen bleiben. Nach dieser Zeit ohne Beruehrung springt
// die Ansicht zurueck auf Check-in.
const INAKTIV_MS = 5 * 60 * 1000;

const LEVEL_MEDIUM = 8;
const LEVEL_HIGH = 18;

function levelInfo(count) {
  if (count >= LEVEL_HIGH) return { label: 'Voll', cls: 'bg-red-500' };
  if (count >= LEVEL_MEDIUM) return { label: 'Mittel', cls: 'bg-amber-500' };
  return { label: 'Ruhig', cls: 'bg-emerald-500' };
}

function timeDE(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }); }
  catch { return ''; }
}

export default function EmpfangPage() {
  const navigate = useNavigate();
  const [studioQr, setStudioQr] = useState(null);
  const [qrGross, setQrGross] = useState(false);
  const [uscOffen, setUscOffen] = useState(false);
  useEffect(() => { checkinsAPI.getStudioQRCode().then(r => setStudioQr(r.data.qr_code)).catch(() => {}); }, []);
  const [present, setPresent] = useState([]);
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('checkin'); // 'checkin' | 'closer' | 'verkauf' | 'praemien' | 'shop'
  const [verkaufVorbelegung, setVerkaufVorbelegung] = useState(null);
  const { user, userPermissions, isAdminPermissions } = useAuth();
  const darf = (key) => !key || isAdminPermissions || user?.role === 'admin'
    || (userPermissions ? !!userPermissions[key] : false);

  // Aus dem Briefing direkt ins Gespraech — im Empfang bleiben, Werkzeug wechseln
  const verkaufStarten = (lead) => {
    const q = lead.qualifizierung || {};
    setVerkaufVorbelegung({
      lead: lead.id || '',      // Rechner lädt den Lead selbst (30.10.2026)
      name: lead.name || '', telefon: lead.telefon || '', email: lead.email || '',
      zielgruppe: q.fuer_wen === 'kind' ? 'kind' : 'erwachsener',
    });
    setView('verkauf');
  };

  // Inaktivitaets-Ruecksprung: jede Beruehrung setzt den Zaehler zurueck.
  useEffect(() => {
    if (view === 'checkin') return;
    let timer;
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        setView('checkin');
        setVerkaufVorbelegung(null);
        toast.info('Zurück zum Empfang — das Werkzeug lag 5 Minuten unberührt.');
      }, INAKTIV_MS);
    };
    reset();
    // 'p1:aktiv' meldet der Unterschrift-Dialog, solange der Kunde am iPad liest (Vorgang lebt 15 Minuten)
    const events = ['pointerdown', 'keydown', 'touchstart', 'p1:aktiv'];
    events.forEach((e) => window.addEventListener(e, reset));
    return () => {
      clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [view]);

  const fetchPresent = useCallback(async () => {
    try {
      const res = await checkinsAPI.present();
      setPresent(res.data.present || []);
    } catch { /* ignore */ }
  }, []);

  // Erstes Laden der Anwesenheit. Die Personen für den Check-in lädt die Seite nicht mehr
  // auf Vorrat — die Suche fragt bei jeder Eingabe den Server (useMitgliederSuche).
  useEffect(() => { fetchPresent().finally(() => setLoading(false)); }, [fetchPresent]);
  useEffect(() => {
    const t = setInterval(fetchPresent, 30000);
    return () => clearInterval(t);
  }, [fetchPresent]);

  const presentIds = useMemo(() => new Set(present.map(p => p.member_id)), [present]);

  // Sucht serverseitig über ALLE Personen (auch fremde, alte und archivierte Leads)
  const suche = useMitgliederSuche(search, { limit: 15 });

  const handleCheckin = async (m) => {
    setBusyId(m.id);
    try {
      await checkinsAPI.checkin({ member_id: m.id, method: 'empfang' });
      setSearch('');
      // Prämie ansprechen (30.09.2026): Stand kommt mit der Anwesenheitsliste
      let hinweis = '';
      try {
        const res = await checkinsAPI.present();
        const liste = res.data.present || [];
        setPresent(liste);
        const pr = liste.find(x => x.member_id === m.id)?.praemie;
        if (pr) hinweis = pr.einloesbar ? ` · ${pr.punkte} Punkte – ${pr.einloesbar} einlösbar` : ` · ${pr.punkte} Punkte`;
      } catch { /* Hinweis ist Beiwerk */ }
      toast.success(`${m.name} eingecheckt${hinweis}`, { duration: hinweis.includes('einlösbar') ? 8000 : 4000 });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Check-in fehlgeschlagen');
    } finally { setBusyId(null); }
  };

  const handleCheckout = async (p) => {
    setBusyId(p.member_id);
    try {
      await checkinsAPI.checkout(p.member_id);
      toast.success(`${p.member_name} ausgecheckt`);
      setPresent(prev => prev.filter(x => x.member_id !== p.member_id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Check-out fehlgeschlagen');
    } finally { setBusyId(null); }
  };

  const lvl = levelInfo(present.length);

  return (
    <div className="space-y-4 fade-in" data-testid="empfang-page">
      {/* Kopf: Titel + Live-Auslastung */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Empfang</h1>
          <p className="text-muted-foreground text-sm">Check-in · Check-out · Auslastung · Statistik</p>
        </div>
        <div className="flex items-center gap-2">
        {/* Urban Sports Club (30.09.2026): Gast erfassen — belegt den Kursplatz, zaehlt nicht als Mitglieder-Check-in */}
        <Button variant="outline" className="h-auto py-2 px-3 flex flex-col items-center leading-tight" onClick={() => setUscOffen(true)} data-testid="empfang-usc">
          <span className="text-sm font-bold">USC-Gast</span><span className="text-[11px] text-muted-foreground">erfassen</span>
        </Button>
        {/* Studio-QR (28.09.2026): Mitglieder scannen ihn mit der App ("Am Empfang einchecken"); antippen = Vollbild */}
        {studioQr && (
          <button type="button" onClick={() => setQrGross(true)} className="flex items-center gap-2 bg-card border border-border rounded-xl px-2 py-1.5 hover:bg-muted" title="Studio-QR groß anzeigen" data-testid="empfang-studio-qr">
            <img src={studioQr} alt="Studio-QR-Code zum Einchecken" className="w-14 h-14" />
            <span className="text-xs text-left leading-tight"><b>Studio-QR</b><br /><span className="text-muted-foreground">zum Scannen<br />antippen</span></span>
          </button>
        )}
        <div className="flex items-center gap-2 bg-card border border-border rounded-xl px-4 py-2">
          <Users className="w-5 h-5 text-muted-foreground" />
          <span className="text-3xl font-bold tabular-nums">{present.length}</span>
          <span className="text-sm text-muted-foreground">im Studio</span>
          <span className={`ml-1 text-xs font-semibold text-white px-2 py-0.5 rounded-full ${lvl.cls}`}>{lvl.label}</span>
        </div>
        </div>
      </div>

      <UscGastDialog open={uscOffen} onClose={() => setUscOffen(false)} />

      {qrGross && studioQr && (
        <div className="fixed inset-0 z-[200] bg-white flex flex-col items-center justify-center p-6" onClick={() => setQrGross(false)} data-testid="empfang-studio-qr-gross">
          <button type="button" className="absolute top-4 right-4 h-12 w-12 rounded-full border border-gray-300 flex items-center justify-center text-black" aria-label="Schließen"><X className="w-6 h-6" /></button>
          <img src={studioQr} alt="Studio-QR-Code zum Einchecken" className="w-[min(80vw,70vh)] h-[min(80vw,70vh)]" style={{ imageRendering: 'pixelated' }} />
          <p className="mt-4 text-2xl font-bold text-black text-center">Mit der P1 App scannen</p>
          <p className="text-base text-gray-600 text-center">Home → „Am Empfang einchecken“</p>
        </div>
      )}

      {view === 'checkin' ? (
        <>
          {/* Werkzeuge — öffnen bei Bedarf groß */}
          <div className="flex flex-wrap gap-2">
            {TOOLS.filter((t) => darf(t.benoetigt)).map((t) => {
              const Icon = t.icon;
              return (
                <Button key={t.key} variant="outline" size="lg" className="gap-2 h-12 text-base" onClick={() => { setVerkaufVorbelegung(null); setView(t.key); }} data-testid={`empfang-tool-${t.key}`}>
                  <Icon className="w-5 h-5" /> {t.label}
                </Button>
              );
            })}
          </div>

          {/* Check-in | Check-out */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Check-in */}
            <Card className="border-border/60">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <LogIn className="w-5 h-5 text-emerald-600" />
                  <h2 className="text-lg font-semibold">Check-in</h2>
                </div>
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Name oder Mitgliedsnummer…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9 h-14 text-base"
                    data-testid="empfang-search"
                  />
                </div>
                <div className="space-y-2 max-h-[55vh] overflow-y-auto">
                  {suche.leer ? (
                    <p className="text-center text-sm text-muted-foreground py-10">Zum Einchecken oben ein Mitglied suchen.</p>
                  ) : suche.zuKurz ? (
                    <p className="text-center text-sm text-muted-foreground py-10">Mindestens 2 Zeichen eingeben.</p>
                  ) : suche.laedt ? (
                    <p className="text-center text-sm text-muted-foreground py-10" data-testid="empfang-search-laedt">Sucht…</p>
                  ) : suche.fehler ? (
                    <div className="text-center py-8 space-y-3" data-testid="empfang-search-fehler">
                      {suche.keinRecht ? (
                        <p className="text-sm text-red-600 dark:text-red-400">Dieses Konto darf am Empfang nicht suchen. Bitte die Leitung ansprechen.</p>
                      ) : (
                        <>
                          <p className="text-sm text-red-600 dark:text-red-400">Die Suche hat nicht geantwortet. Es wurde nicht gesucht.</p>
                          <Button variant="outline" className="h-11" onClick={suche.erneut}>Erneut suchen</Button>
                        </>
                      )}
                    </div>
                  ) : suche.treffer.length === 0 ? (
                    <p className="text-center text-sm text-muted-foreground py-10" data-testid="empfang-search-leer">Kein Mitglied gefunden.</p>
                  ) : suche.treffer.map((m) => {
                    const isPresent = presentIds.has(m.id);
                    const zusatz = trefferZusatz(m);
                    return (
                      <button
                        key={m.id}
                        disabled={busyId === m.id || isPresent}
                        onClick={() => handleCheckin(m)}
                        className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl border text-left transition-colors ${
                          isPresent ? 'border-border/40 bg-muted/40 opacity-60' : 'border-border/60 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 active:bg-emerald-100'
                        }`}
                        data-testid={`empfang-checkin-${m.id}`}
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">
                            {m.name}
                            {m.archiviert && <span className="ml-2 align-middle text-[10px] font-semibold uppercase tracking-wide text-muted-foreground border border-border rounded px-1.5 py-0.5">Archiv</span>}
                            {/* E13 (15.10.2026): seit vier Wochen dabei, kein Ernährungs-Abo — anbieten */}
                            {/* E23: ein Tipp auf den Hinweis öffnet „Neuer Kunde in einem Fluss“ für diese Person (stoppt den Check-in-Klick) */}
                            {m.ernaehrung_anbieten && <span role="link" tabIndex={0} className="ml-2 align-middle text-[10px] font-semibold uppercase tracking-wide text-amber-800 bg-amber-500/10 border border-amber-300 rounded px-1.5 py-0.5 cursor-pointer hover:bg-amber-500/20" data-testid="empfang-ernaehrung-anbieten"
                              onClick={(e) => { e.stopPropagation(); e.preventDefault(); navigate(`/ernaehrung/abschluss?lead_id=${m.id}`); }}
                              onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); e.preventDefault(); navigate(`/ernaehrung/abschluss?lead_id=${m.id}`); } }}>Ernährung anbieten</span>}
                          </p>
                          {zusatz && <p className="text-xs text-muted-foreground truncate">{zusatz}</p>}
                        </div>
                        {isPresent
                          ? <span className="text-xs text-muted-foreground flex-shrink-0">bereits da</span>
                          : <LogIn className="w-5 h-5 text-emerald-600 flex-shrink-0" />}
                      </button>
                    );
                  })}
                  {suche.mehr && (
                    <p className="text-center text-xs text-muted-foreground py-2" data-testid="empfang-search-mehr">Es gibt weitere Treffer – bitte genauer suchen.</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Anwesend / Check-out */}
            <Card className="border-border/60">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <LogOut className="w-5 h-5 text-red-500" />
                    <h2 className="text-lg font-semibold">Anwesend ({present.length})</h2>
                  </div>
                  <Button variant="ghost" size="sm" className="gap-1.5" onClick={fetchPresent} data-testid="empfang-refresh">
                    <RefreshCw className="w-4 h-4" /> Aktualisieren
                  </Button>
                </div>
                <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                  {loading ? (
                    <p className="text-center text-sm text-muted-foreground py-10">Lädt…</p>
                  ) : present.length === 0 ? (
                    <p className="text-center text-sm text-muted-foreground py-10">Aktuell ist niemand eingecheckt.</p>
                  ) : present.map((p) => (
                    <button
                      key={p.id}
                      disabled={busyId === p.member_id}
                      onClick={() => handleCheckout(p)}
                      className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-border/60 text-left transition-colors hover:bg-red-50 dark:hover:bg-red-900/20 active:bg-red-100"
                      data-testid={`empfang-checkout-${p.member_id}`}
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{p.member_name}</p>
                        <p className="text-xs text-muted-foreground">{p.wieder_da_seit ? `wieder da seit ${timeDE(p.wieder_da_seit)} Uhr · Check-in ${timeDE(p.checked_in_at)}` : `seit ${timeDE(p.checked_in_at)} Uhr`}{p.praemie ? ` · ${p.praemie.punkte} P` : ''}</p>
                        {p.praemie?.einloesbar && <p className="text-xs font-medium text-emerald-600 dark:text-emerald-400 truncate">{p.praemie.einloesbar} einlösbar</p>}
                      </div>
                      <span className="flex items-center gap-1.5 text-xs font-medium text-red-500 flex-shrink-0">
                        <LogOut className="w-5 h-5" /> Auschecken
                      </span>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Infrarotkabine (16.10.2026): heute und morgen, nur lesen — der Name öffnet den Lead */}
          <InfrarotKarte />
        </>
      ) : (
        <>
          <Button variant="outline" size="lg" className="gap-2 h-12" onClick={() => { setView('checkin'); setVerkaufVorbelegung(null); }} data-testid="empfang-back">
            <ArrowLeft className="w-5 h-5" /> Zurück zum Empfang
          </Button>
          {view === 'closer' && <CloserPage onVerkauf={verkaufStarten} />}
          {view === 'verkauf' && <VerkaufPage vorbelegung={verkaufVorbelegung} />}
          {view === 'praemien' && <PraemienPage />}
          {view === 'shop' && <ShopPage />}
          {view === 'statistik' && <CheckInPage eingebettet />}
        </>
      )}
    </div>
  );
}
